import React, { useState, useEffect } from 'react';
import { Typography, Row, Col, Card, Spin, Alert } from 'antd';
import { getEvents } from '../services/eventsService';
import styled from 'styled-components';
import dayjs from 'dayjs';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const RootContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.pageBackground || '#ffffff'};
`;

const WidgetContainer = styled.div`
  padding: 24px;
  height: calc(100vh - 48px); // Account for padding
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollContainer = styled.div`
  flex: 1;
  min-height: 0; // Important for proper scrolling
  overflow-x: ${props => (props.scrollDirection === 'horizontal' ? 'auto' : 'hidden')};
  overflow-y: auto;
  padding-bottom: 16px; // Add space for the scrollbar

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
    display: block; // Ensure scrollbar is always visible
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.accent || '#1890ff'};
    border-radius: 4px;
    &:hover {
      background: ${props => (props.theme.accent ? `${props.theme.accent}dd` : '#1890ffdd')};
    }
  }

  &::-webkit-scrollbar-track {
    background: ${props => props.theme.background || '#ffffff'};
    border-radius: 4px;
    margin: 0 24px; // Add some margin to the track
  }
`;

const EventsRow = styled(Row)`
  flex-wrap: ${props => (props.scrollDirection === 'horizontal' ? 'nowrap' : 'wrap')};
  ${props =>
    props.scrollDirection === 'horizontal' &&
    `
    width: max-content;
    padding-bottom: 8px; // Add space for scrollbar
    & > div {
      flex: 0 0 auto;
    }
  `}
`;

const CalendarEvent = styled(Card)`
  background: ${props => props.theme.eventBackground || '#ffffff'};
  ${props =>
    props.borderWidth > 0
      ? `
    border-color: ${props.theme.accent || '#1890ff'};
    border-width: ${props.borderWidth}px;
  `
      : 'border: none;'}
  margin-bottom: 8px;
  width: ${props => (props.scrollDirection === 'horizontal' ? '300px' : '100%')};

  ${props =>
    props.layout === 'list' &&
    `
    display: flex;
    flex-direction: row;
    .ant-card-cover {
      flex: 0 0 200px;
      margin: 0;
      height: 150px;
      img {
        height: 100%;
        object-fit: cover;
      }
    }
    .ant-card-body {
      flex: 1;
      padding: 16px;
    }
  `}

  ${props =>
    props.layout === 'agenda' &&
    `
    .ant-card-body {
      padding: 12px;
    }
    .ant-card-meta-title {
      font-size: 14px;
      margin-bottom: 4px !important;
    }
    .ant-card-meta-description {
      font-size: 12px;
    }
  `}

  ${props =>
    props.layout === 'grid' &&
    `
    .ant-card-cover {
      height: 200px;
      overflow: hidden;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  `}

  .ant-card-meta-title {
    color: ${props => props.theme.text || '#000000'};
    margin-bottom: 8px;
  }
  .ant-card-meta-description {
    color: ${props => props.theme.text || '#000000'};
    opacity: 0.85;
  }
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  background: ${props => props.theme.pageBackground || '#ffffff'};
  padding: 0 24px;

  .slick-slider {
    height: 100%;
  }

  .slick-list {
    height: 100%;
  }

  .slick-track {
    height: 100%;
    display: flex;
    align-items: center;
  }

  .slick-slide {
    padding: 0 8px;
    > div {
      height: 100%;
    }
  }

  .slick-arrow {
    z-index: 1;
    width: 30px;
    height: 30px;
    &:before {
      color: ${props => props.theme.accent || '#1890ff'};
      font-size: 30px;
    }
    &:hover:before {
      color: ${props => (props.theme.accent ? `${props.theme.accent}dd` : '#1890ffdd')};
    }
  }

  .slick-prev {
    left: -10px;
  }

  .slick-next {
    right: -10px;
  }

  .slick-dots {
    bottom: -25px;
    li {
      button:before {
        color: ${props => props.theme.accent || '#1890ff'};
      }
      &.slick-active button:before {
        color: ${props => props.theme.accent || '#1890ff'};
      }
    }
  }
`;

const SliderEvent = styled(Card)`
  height: 200px;
  margin: 10px 0;
  background: ${props => props.theme.eventBackground || '#ffffff'};
  ${props =>
    props.borderWidth > 0
      ? `
    border-color: ${props.theme.accent || '#1890ff'};
    border-width: ${props.borderWidth}px;
  `
      : 'border: none;'}

  .ant-card-cover {
    height: 120px;
    overflow: hidden;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .ant-card-body {
    padding: 12px;
  }

  .ant-card-meta-title {
    color: ${props => props.theme.text || '#000000'};
    margin-bottom: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
  }

  .ant-card-meta-description {
    color: ${props => props.theme.text || '#000000'};
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
`;

function SliderView({ events, theme, borderWidth }) {
  const formatDate = dateString => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    autoplay: true,
    autoplaySpeed: 15000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <SliderContainer theme={theme}>
      <Slider {...settings}>
        {events.map(event => (
          <div key={event.id}>
            <SliderEvent
              theme={theme}
              borderWidth={borderWidth}
              hoverable
              cover={
                <img
                  alt={event.name}
                  src={event.image_url || 'https://source.unsplash.com/random?event'}
                />
              }
              onClick={() => event.ticket_url && window.open(event.ticket_url, '_blank')}
            >
              <Card.Meta title={event.name} description={formatDate(event.start_date)} />
            </SliderEvent>
          </div>
        ))}
      </Slider>
    </SliderContainer>
  );
}

function CalendarEventCard({
  event,
  theme,
  showDescription,
  scrollDirection,
  layout,
  borderWidth,
}) {
  const formatDate = dateString => {
    return dayjs(dateString).format('MMM D, YYYY h:mm A');
  };

  const imageElement = layout !== 'agenda' && (
    <img
      alt={event.name}
      src={event.image_url || 'https://source.unsplash.com/random?event'}
      style={layout === 'list' ? { width: '200px' } : undefined}
    />
  );

  return (
    <CalendarEvent
      theme={theme}
      scrollDirection={scrollDirection}
      layout={layout}
      borderWidth={borderWidth}
      hoverable
      cover={imageElement}
      onClick={() => event.ticket_url && window.open(event.ticket_url, '_blank')}
    >
      <Card.Meta
        title={event.name}
        description={
          <>
            <div style={{ fontWeight: 500, marginBottom: layout === 'agenda' ? 0 : 4 }}>
              {formatDate(event.start_date)}
            </div>
            {showDescription && event.description && layout !== 'agenda' && (
              <div style={{ marginTop: 8 }}>{event.description}</div>
            )}
          </>
        }
      />
    </CalendarEvent>
  );
}

function CalendarEmbed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get configuration from URL parameters
  const params = new URLSearchParams(window.location.search);
  const layout = params.get('layout') || 'grid';
  const theme = JSON.parse(decodeURIComponent(params.get('theme') || '{}')) || {
    background: '#ffffff',
    eventBackground: '#ffffff',
    accent: '#1890ff',
    text: '#000000',
  };
  const showDescription = params.get('showDescription') === 'true';
  const scrollDirection = params.get('scrollDirection') || 'horizontal';

  // Add border width parameter
  const cardBorderWidth = parseInt(params.get('cardBorderWidth')) || 1;

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await getEvents();
        setEvents(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert message={error} type="error" />
      </div>
    );
  }

  if (layout === 'slider') {
    return (
      <RootContainer theme={theme}>
        <SliderView events={events} theme={theme} borderWidth={cardBorderWidth} />
      </RootContainer>
    );
  }

  return (
    <RootContainer theme={theme}>
      <WidgetContainer>
        <ScrollContainer theme={theme} scrollDirection={scrollDirection}>
          <EventsRow gutter={[16, 16]} scrollDirection={scrollDirection}>
            {events.map(event => (
              <Col
                key={event.id}
                xs={scrollDirection === 'horizontal' ? undefined : 24}
                sm={scrollDirection === 'horizontal' ? undefined : layout === 'grid' ? 12 : 24}
                md={scrollDirection === 'horizontal' ? undefined : layout === 'grid' ? 8 : 24}
                lg={scrollDirection === 'horizontal' ? undefined : layout === 'grid' ? 6 : 24}
                style={scrollDirection === 'horizontal' ? { marginRight: 16 } : undefined}
              >
                <CalendarEventCard
                  event={event}
                  theme={theme}
                  showDescription={showDescription}
                  scrollDirection={scrollDirection}
                  layout={layout}
                  borderWidth={cardBorderWidth}
                />
              </Col>
            ))}
          </EventsRow>
        </ScrollContainer>
      </WidgetContainer>
    </RootContainer>
  );
}

export default CalendarEmbed;
